/**
 * Recurring tickets: the templates that open a job on a schedule.
 *
 * The port of `Views/Controls/RecurringTicketsSection.xaml` + `RecurringTicketsSectionViewModel`.
 *
 * A template is not a ticket. It opens a real one per period, carrying a period key that makes a
 * second pass over the same period open nothing - so editing a template changes what it opens next
 * period and never restates what it already opened, and deleting one keeps every ticket it produced,
 * because those are real work.
 */
import type { StackPanel } from 'webwin';
import * as crm from '../../api/crm.js';
import { RecurrenceInterval, type RecurringTicketDto } from '../../contracts.gen.js';
import { plural } from '../../format.js';
import { isoDay, isoDayOf, middayUtcFromIso, periodLabel, todayIso } from '../../recurrence.js';
import { CardList } from '../../ui/CardList.js';
import { Card } from '../../ui/Card.js';
import { chip } from '../../ui/Chip.js';
import { dateBox } from '../../ui/DateBox.js';
import { confirm } from '../../ui/dialogs.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, comboBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, relayout, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import {
  INTERVAL_NAMES,
  INTERVALS,
  loadCustomerChoices,
  PRIORITIES,
  PRIORITY_NAMES,
  readSchedule,
  endsClause,
  failure,
  type CustomerChoices,
  type RecurringSection,
  type RecurringSectionOptions
} from './recurring.js';

export function recurringTicketsSection(options: RecurringSectionOptions): RecurringSection {
  const { ctx, lockCustomer, onChanged } = options;
  let customerFilterId = options.customerFilterId;

  let choices: CustomerChoices = { names: [], ids: [] };
  let editingId = 0;
  let busy = false;

  const status = new StatusLine();

  // ---- header ----
  const runButton = button('Open due tickets now', () => void run());
  const newButton = button('New recurring ticket', () => beginNew(), true);
  // Title, sentence, then the buttons on a wrapping row of their own. Beside the buttons the sentence
  // was clipped - Webwin measures a filling child's height at the row's full width - and the two
  // buttons do not fit beside a title on a phone anyway.
  const header = stackOf(
    vstack({ spacing: GAP.sm }),
    h2('Recurring tickets'),
    muted('Work that comes round on a schedule - a backup check, a quarterly review. Each period opens its own ticket.'),
    stackOf(buttonRow(), newButton, runButton)
  );

  // ---- editor ----
  const editorTitle = h2('New recurring ticket');
  const titleBox = textBox();
  const descriptionBox = textBox();
  const priorityPicker = comboBox<string>([...PRIORITY_NAMES], { selectedIndex: 1 });
  const customerPicker = comboBox<string>([]);
  const intervalPicker = comboBox<string>([...INTERVAL_NAMES], {
    selectedIndex: 0,
    onSelectionChanged: () => updatePreview()
  });
  const dayBox = textBox({ text: '1' });
  const start = dateBox({ iso: todayIso(), onChange: () => updatePreview() });
  const end = dateBox({ onChange: () => updatePreview() });
  const activeCheck = checkBox('Active', true);
  const appendCheck = checkBox('Put the period on the title', true);
  const notesBox = textBox();
  const preview = muted('', 'subtle');

  titleBox.on('textchanged', () => updatePreview());
  dayBox.on('textchanged', () => updatePreview());
  appendCheck.on('change', () => updatePreview());

  const customerField = field('Customer', customerPicker);
  customerField.visible = !lockCustomer;

  const editor: Card = card({ visible: false });
  editor.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      editorTitle,
      fieldRow(field('Title', titleBox), customerField),
      field('Description', descriptionBox),
      fieldRow(
        field('Repeats', intervalPicker),
        field('Day of the month', dayBox),
        field('Priority', priorityPicker)
      ),
      fieldRow(field('Starting', start.picker), field('Ending (optional)', end.picker)),
      stackOf(hstack({ spacing: GAP.lg }), activeCheck, appendCheck),
      preview,
      field('Notes', notesBox),
      stackOf(
        buttonRow(),
        button('Save', () => void save(), true),
        button('Cancel', () => closeEditor())
      )
    )
  );

  // ---- list ----
  const list = new CardList<RecurringTicketDto>({
    key: t => t.id,
    build: t => buildRow(t)
  });
  const empty = new EmptyState(
    'No recurring tickets yet. Add one for a job that comes round, and it opens itself on the day.'
  );

  const panel = stackOf(
    vstack({ spacing: GAP.md }),
    header,
    status.label,
    editor,
    empty.panel,
    list.panel
  );

  // -----------------------------------------------------------------
  //  Rows
  // -----------------------------------------------------------------

  function buildRow(t: RecurringTicketDto): Card {
    const heading = inlineRow();
    heading.controls.add(value(t.title, { font: FONT.bold }));
    if (!lockCustomer) heading.controls.add(value(t.customerName, { font: FONT.small, role: 'muted' }));
    if (!t.active) heading.controls.add(chip('paused', 'warn'));

    const detail = [
      `${t.intervalLabel} on day ${t.dayOfMonth}`,
      t.nextDueUtc ? `next ${isoDayOf(t.nextDueUtc)}` : 'nothing further due',
      `${plural(t.generatedCount, 'ticket')} opened so far`,
      PRIORITY_NAMES[t.priority] ?? ''
    ]
      .filter(Boolean)
      .join('  ·  ');

    const left = stackOf(vstack({ spacing: GAP.xs }), heading, muted(detail));

    const actions = hstack({ spacing: GAP.sm });
    actions.controls.add(button('Edit', () => beginEdit(t)));
    actions.controls.add(button('Remove', () => void remove(t)));

    const row = splitRow();
    fillWith(row, left);
    row.controls.add(actions);

    const shell = card({ padding: { left: 12, top: 12, right: 12, bottom: 12 } });
    shell.controls.add(row);
    return shell;
  }

  // -----------------------------------------------------------------
  //  Editor
  // -----------------------------------------------------------------

  function selectedCustomerId(): number | null {
    const index = customerPicker.selectedIndex;
    return index >= 0 && index < choices.ids.length ? choices.ids[index]! : null;
  }

  function beginNew(): void {
    editingId = 0;
    editorTitle.text = 'New recurring ticket';
    titleBox.text = '';
    descriptionBox.text = '';
    priorityPicker.selectedIndex = 1;
    customerPicker.selectedIndex = preselectedCustomerIndex();
    intervalPicker.selectedIndex = 0;
    dayBox.text = '1';
    start.set(todayIso());
    end.set(null);
    activeCheck.checked = true;
    appendCheck.checked = true;
    notesBox.text = '';
    openEditor();
  }

  function beginEdit(t: RecurringTicketDto): void {
    editingId = t.id;
    editorTitle.text = `Edit recurring ticket #${t.id}`;
    titleBox.text = t.title;
    descriptionBox.text = t.description;
    priorityPicker.selectedIndex = Math.max(0, PRIORITIES.indexOf(t.priority));
    customerPicker.selectedIndex = Math.max(0, choices.ids.indexOf(t.customerId));
    intervalPicker.selectedIndex = Math.max(0, INTERVALS.indexOf(t.interval));
    dayBox.text = String(t.dayOfMonth);
    start.set(isoDayOf(t.startUtc));
    end.set(t.endUtc ? isoDayOf(t.endUtc) : null);
    activeCheck.checked = t.active;
    appendCheck.checked = t.appendPeriodToTitle;
    notesBox.text = t.notes;
    openEditor();
  }

  function preselectedCustomerIndex(): number {
    if (customerFilterId === undefined) return choices.ids.length > 0 ? 0 : -1;
    return Math.max(0, choices.ids.indexOf(customerFilterId));
  }

  function openEditor(): void {
    updatePreview();
    editor.visible = true;
    relayout(panel);
    titleBox.focus();
  }

  function closeEditor(): void {
    editor.visible = false;
    relayout(panel);
  }

  /**
   * "First ticket on 2026-10-01 as "Backup check - October 2026", then monthly."
   *
   * Computed from the same schedule the server generates from, which is why the title shown is the
   * title that will really be used - period suffix included, since that is the part a technician
   * cannot work out by reading the form.
   */
  function updatePreview(): void {
    const reading = readSchedule(intervalPicker.selectedIndex, dayBox.text, start.iso(), end.iso());
    if (!reading.first) {
      preview.text = reading.problem;
      return;
    }

    const interval: RecurrenceInterval =
      INTERVALS[Math.max(0, Math.min(INTERVALS.length - 1, intervalPicker.selectedIndex))]!;
    const typed = titleBox.text.trim();
    let title = typed || '(untitled)';
    if (appendCheck.checked) title += ' - ' + periodLabel(interval, reading.first);

    let message = `First ticket on ${isoDay(reading.first)} as "${title}", then ${INTERVAL_NAMES[
      intervalPicker.selectedIndex
    ]?.toLowerCase()}.`;
    if (reading.owed > 0) {
      message += ` Saving opens ${plural(reading.owed, 'ticket')} already owed since the start date.`;
    }
    message += endsClause(end.iso());
    preview.text = message;
  }

  async function save(): Promise<void> {
    if (busy) return;
    const title = titleBox.text.trim();
    if (!title) {
      status.error('A recurring ticket needs a title.');
      return;
    }
    const customerId = selectedCustomerId();
    if (customerId === null) {
      status.error('Pick the customer the ticket is for.');
      return;
    }
    const day = Number.parseInt(dayBox.text, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) {
      status.error('Day of the month must be between 1 and 31.');
      return;
    }

    const startUtc = middayUtcFromIso(start.iso()) ?? middayUtcFromIso(todayIso())!;
    const endUtc = middayUtcFromIso(end.iso());

    busy = true;
    status.busy('Saving…');
    try {
      const body = {
        customerId,
        title,
        description: descriptionBox.text.trim(),
        priority: PRIORITIES[Math.max(0, priorityPicker.selectedIndex)]!,
        interval: INTERVALS[Math.max(0, intervalPicker.selectedIndex)]!,
        dayOfMonth: day,
        startUtc: startUtc.toISOString(),
        endUtc: endUtc ? endUtc.toISOString() : null,
        active: activeCheck.checked,
        appendPeriodToTitle: appendCheck.checked,
        notes: notesBox.text.trim()
      };
      if (editingId === 0) await crm.createRecurringTicket(body);
      else await crm.updateRecurringTicket(editingId, body);
      if (ctx.cancelled()) return;
      status.ok(`Saved ${title}. Any tickets it already owed have been opened.`);
      closeEditor();
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not save the recurring ticket', error));
    } finally {
      busy = false;
    }
  }

  async function remove(t: RecurringTicketDto): Promise<void> {
    const ok = await confirm(ctx.desktop, {
      title: 'Remove recurring ticket',
      message:
        `Stop "${t.title}" for ${t.customerName}? ` +
        `The ${plural(t.generatedCount, 'ticket')} it already opened are kept.`
    });
    if (!ok || ctx.cancelled()) return;

    status.busy('Removing…');
    try {
      await crm.deleteRecurringTicket(t.id);
      if (ctx.cancelled()) return;
      status.ok(`Removed the recurring ticket ${t.title}. Tickets already opened are unchanged.`);
      if (editingId === t.id) closeEditor();
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not remove that recurring ticket', error));
    }
  }

  /**
   * Runs the schedule now.
   *
   * It runs hourly on its own, and every save runs it too - this button exists for the technician
   * who has just fixed a start date and wants to see the result rather than wait out the hour.
   */
  async function run(): Promise<void> {
    if (busy) return;
    busy = true;
    status.busy('Opening due tickets…');
    try {
      const result = await crm.runRecurringTickets();
      if (ctx.cancelled()) return;
      status.ok(
        result.created === 0
          ? `Nothing due - all ${plural(result.templatesConsidered, 'recurring ticket')} are up to date.`
          : `Opened ${plural(result.created, 'ticket')} from ${plural(
              result.templatesConsidered,
              'template'
            )}.`
      );
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not open the recurring tickets', error));
    } finally {
      busy = false;
    }
  }

  // -----------------------------------------------------------------
  //  Loading
  // -----------------------------------------------------------------

  async function refresh(): Promise<void> {
    try {
      const all = await crm.getRecurringTickets(ctx.dispose.signal());
      if (ctx.cancelled()) return;
      const mine =
        customerFilterId === undefined ? all : all.filter(t => t.customerId === customerFilterId);
      list.set(mine);
      empty.update(mine.length);
      relayout(panel);
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not load the recurring tickets', error));
    }
  }

  async function initialize(): Promise<void> {
    choices = await loadCustomerChoices(ctx.dispose.signal());
    if (ctx.cancelled()) return;
    customerPicker.items = choices.names;
    customerPicker.selectedIndex = preselectedCustomerIndex();
    await refresh();
  }

  /** Narrows the list, and preselects the editor, to another customer. Never moves an open editor. */
  async function setCustomerFilter(id: number | undefined): Promise<void> {
    customerFilterId = id;
    await refresh();
  }

  return { panel, initialize, refresh, setCustomerFilter };
}

